import { TradingPartner } from "components/csd-enrollment/steps/insurance/ConnectHealthPlanStep/types"
import { InsurancePlanInformationRowData } from "./InsurancePlanInformationRow"
import moment from "moment"
import { getMemberCoverageLabelFromId } from "components/csd-enrollment/steps/MemberCoverageTypeStep"
import { getPartnerCoverageTypeLabelFromId } from "components/csd-enrollment/steps/PartnerCoverageTypeStep"
import { getHdhpStatusLabelFromId } from "components/csd-enrollment/steps/HasHdhpStep"
import { getHsaContributionLabelFromId } from "components/csd-enrollment/steps/HsaContributionStatusStep"
import { getMemberPlanSourceLabelFromId } from "components/csd-enrollment/steps/MemberPlanSourceStep"
import { getPartnerPlanSourceLabelFromId } from "components/csd-enrollment/steps/PartnerPlanSourceStep"
import { getSexLabelFromId } from "components/csd-enrollment/steps/insurance/DobSexStep"

const yesOrNo = (val: boolean): string => (val ? "Yes" : "No")

const formatDate = (date: string): string => moment(date).format("ll")

const getFormattedDateRangeOrNull = (startDate: string, endDate: string): string =>
  startDate && endDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : null

export const getInsurancePlanDetailsData = (
  userInfo: any,
  tradingPartners: TradingPartner[]
): InsurancePlanInformationRowData[] => {
  const memberTradingPartnerName = tradingPartners?.find(
    (tp) => tp.tradingPartnerPk === userInfo.selectedTradingPartnerPk
  )?.description

  const partnerTradingPartnerName = tradingPartners?.find(
    (tp) => tp.tradingPartnerPk === userInfo.partnerHealthPlanSelectedTradingPartnerPk
  )?.description

  return [
    {
      headerLabel: "Health insurance name",
      memberData: memberTradingPartnerName,
      partnerData: partnerTradingPartnerName
    },
    {
      headerLabel: "Member ID",
      memberData: userInfo.memberId,
      partnerData: userInfo.partnerHealthPlanMemberId
    },
    {
      headerLabel: "Sex",
      memberData: getSexLabelFromId(userInfo.sex),
      partnerData: getSexLabelFromId(userInfo.partnerSex),
      helperText: "(as stated on your health insurance plan)"
    },
    {
      headerLabel: "Date of birth",
      memberData: formatDate(userInfo.dateOfBirth),
      partnerData: formatDate(userInfo.partnerDateOfBirth),
      helperText: "(as stated on your health insurance plan)"
    }
  ]
}

export const getPlanProviderDetailsData = (userInfo: any): InsurancePlanInformationRowData[] => {
  return [
    {
      headerLabel: "Where do you get your health insurance?",
      memberData: getMemberPlanSourceLabelFromId(userInfo.memberPlanSourceSelectValue),
      partnerData: getPartnerPlanSourceLabelFromId(userInfo.partnerPlanSourceSelectValue)
    },
    {
      headerLabel: "Does your health plan cover just you or does it cover other people as well?",
      memberData: getMemberCoverageLabelFromId(userInfo.memberCoverageTypeSelectValue),
      partnerData: getPartnerCoverageTypeLabelFromId(userInfo.partnerCoverageTypeSelectValue)
    },
    {
      headerLabel: "Are you currently enrolled in a high deductible health plan? (HDHP)?",
      memberData: getHdhpStatusLabelFromId(userInfo.memberHasHDHP),
      partnerData: getHdhpStatusLabelFromId(userInfo.partnerHasHDHP),
      tooltipText: [
        "An HDHP is a major medical plan that meets minimum deductible and maximum out-of-pocket expense limitations set by the IRS each year. The current minimum deductible is at least $1,400 for an individual or at least $2,800 for a family, while out-of-pocket expenses can't total more than $7,000 for an individual or $14,000 for a family.",
        "If you’re not sure about your plan details, we’ll connect you with the Care Team to help you figure it out."
      ]
    },
    {
      headerLabel: "Are you or your employer contributing to a health savings account (HSA) associated with your HDHP?",
      memberData: getHsaContributionLabelFromId(userInfo.memberHsaContributionStatus),
      partnerData: getHsaContributionLabelFromId(userInfo.partnerHsaContributionStatus),
      tooltipText: [
        "An HSA lets you use pre-tax income to pay for eligible medical expenses, and is only available with an HDHP.",
        "If you’re not sure about your plan details, we’ll connect you with the Care Team to help you figure it out."
      ]
    },
    {
      headerLabel: "Are you legally married?",
      memberData: yesOrNo(userInfo.memberHasLegalPartner),
      partnerData: null
    },
    {
      headerLabel: "When does the annual deductible period on your medical plan start and end?",
      memberData: getFormattedDateRangeOrNull(userInfo.memberCoverageStartDate, userInfo.memberCoverageEndDate),
      partnerData: getFormattedDateRangeOrNull(userInfo.partnerCoverageStartDate, userInfo.partnerCoverageEndDate)
    }
  ].filter(({ memberData, partnerData }) => Boolean(memberData) || Boolean(partnerData))
}
